import { z } from "zod";
import { CONTROLS } from "../form-controls/index.js";
import { controlOf, isDisplayOnlyField, isRequiredByRecord, } from "../form-bindings/index.js";
import { answerKeyOf } from "./answers.js";
/**
 * What is wrong with one answer, as the answer check names it: an answer to a
 * field the form does not have, to a display-only field, clearing a field
 * whose record always holds a value, of a kind its control does not take
 * (clearing a check box or switch among them), or a choice it does not offer. Each names the answer first, as a refusal's code
 * names what it is about.
 */
export const AnswerIssueCodeSchema = z.enum([
    "ANSWER_UNKNOWN_FIELD",
    "ANSWER_DISPLAY_ONLY",
    "ANSWER_REQUIRED",
    "ANSWER_WRONG_KIND",
    "ANSWER_UNKNOWN_OPTION",
]);
export const AnswerIssueCode = AnswerIssueCodeSchema.enum;
// A display-only field takes no answer from a form: the server supplies its
// value (ADR-039). A changeable binding's answer is checked like any.
const mustNotBeDisplayOnlyRule = (field) => isDisplayOnlyField(field) ? AnswerIssueCode.ANSWER_DISPLAY_ONLY : undefined;
// Clearing a field bound to a value its record always holds would blank the
// record, so it is refused. A Required the form admin set is a custom field's,
// enforced when the note is finalised.
const mustNotClearARequiredBindingRule = (field, value) => value === null && isRequiredByRecord(field)
    ? AnswerIssueCode.ANSWER_REQUIRED
    : undefined;
// A check box or a switch always answers, on or off, so clearing one is no
// answer it can give.
const mustBeClearableRule = (field, value) => value === null && !CONTROLS[controlOf(field)].isClearable
    ? AnswerIssueCode.ANSWER_WRONG_KIND
    : undefined;
const mustFitItsControlRule = (field, value) => CONTROLS[controlOf(field)].answer.safeParse(value).success
    ? undefined
    : AnswerIssueCode.ANSWER_WRONG_KIND;
const mustBeAnOfferedOptionRule = (field, value) => {
    if (!("options" in field))
        return undefined;
    const offered = new Set(field.options.map((option) => option.value));
    // A cleared single choice is "", which no option's value can be (ADR-032 G).
    const chosen = value === "" ? [] : [value].flat();
    return chosen.every((choice) => offered.has(choice))
        ? undefined
        : AnswerIssueCode.ANSWER_UNKNOWN_OPTION;
};
/**
 * The rules about whether a field takes the answer at all, given or cleared
 * (null), asked first. A new rule is an entry here or in VALUE_RULES.
 */
const PRESENCE_RULES = [
    mustNotBeDisplayOnlyRule,
    mustNotClearARequiredBindingRule,
    mustBeClearableRule,
];
/** The rules a value given must satisfy, never asked of an answer cleared. */
const VALUE_RULES = [
    mustFitItsControlRule,
    mustBeAnOfferedOptionRule,
];
/** What the first of these rules finds wrong, if any finds anything. */
function firstIssue(rules, field, value) {
    for (const rule of rules) {
        const code = rule(field, value);
        if (code)
            return code;
    }
    return undefined;
}
/** The answer's issue: the first rule it breaks, in the order above. */
const firstBroken = (field, value) => firstIssue(PRESENCE_RULES, field, value) ??
    (value === null ? undefined : firstIssue(VALUE_RULES, field, value));
/** What is wrong with these answers to this form, if anything. */
export function checkAnswers(definition, answers) {
    const fields = new Map(definition.modules.flatMap((module) => module.fields.map((field) => [answerKeyOf(module.id, field.id), field])));
    return Object.entries(answers).flatMap(([key, value]) => {
        const field = fields.get(key);
        const code = field
            ? firstBroken(field, value)
            : AnswerIssueCode.ANSWER_UNKNOWN_FIELD;
        return code ? [{ key, code }] : [];
    });
}
